import React from "react";
import { Document, Image, Page, StyleSheet, Text, View } from "@react-pdf/renderer";

const fallbackAccent = "#8b5bff";

const formatPrice = (price, locale = "fr") =>
  new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: Number(price) % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(Number(price || 0));

const buildStyles = (accentColor = fallbackAccent) =>
  StyleSheet.create({
    page: {
      padding: 34,
      fontFamily: "Helvetica",
      fontSize: 10,
      color: "#111827",
      backgroundColor: "#ffffff",
    },
    header: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 18,
      paddingBottom: 14,
      marginBottom: 16,
      borderBottom: `2 solid ${accentColor}`,
    },
    brandBlock: {
      flex: 1,
    },
    title: {
      fontSize: 26,
      fontWeight: 700,
      color: accentColor,
      marginBottom: 6,
      textTransform: "uppercase",
    },
    orgName: {
      fontSize: 14,
      fontWeight: 700,
      color: "#111827",
    },
    generatedAt: {
      marginTop: 5,
      fontSize: 9,
      color: "#6b7280",
    },
    logo: {
      width: 62,
      height: 62,
      objectFit: "contain",
    },
    menuGrid: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: 12,
      alignItems: "flex-start",
    },
    category: {
      width: "48.7%",
      marginBottom: 12,
      breakInside: "avoid",
    },
    categoryTitle: {
      fontSize: 11,
      fontWeight: 700,
      color: "#ffffff",
      backgroundColor: accentColor,
      paddingVertical: 5,
      paddingHorizontal: 8,
      borderRadius: 7,
      marginBottom: 4,
    },
    productRow: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 8,
      paddingVertical: 4,
      paddingHorizontal: 2,
      borderBottom: "1 solid #e5e7eb",
    },
    productName: {
      flex: 1,
      fontSize: 9,
      color: "#111827",
    },
    productPrice: {
      minWidth: 50,
      textAlign: "right",
      fontSize: 9,
      fontWeight: 700,
      color: accentColor,
    },
    empty: {
      padding: 16,
      border: "1 solid #e5e7eb",
      borderRadius: 10,
      color: "#6b7280",
      textAlign: "center",
    },
    footer: {
      position: "absolute",
      left: 34,
      right: 34,
      bottom: 20,
      paddingTop: 8,
      borderTop: "1 solid #e5e7eb",
      color: "#9ca3af",
      fontSize: 8,
      textAlign: "center",
    },
  });

export default function PosMenuPDF({
  organization,
  groups,
  accentColor,
  logoSrc,
  generatedAt,
  labels,
  locale,
}) {
  const styles = buildStyles(accentColor);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <View style={styles.brandBlock}>
            <Text style={styles.title}>{labels.title}</Text>
            <Text style={styles.orgName}>{organization?.organization || ""}</Text>
            <Text style={styles.generatedAt}>
              {labels.generatedAt}: {generatedAt}
            </Text>
          </View>
          {logoSrc ? <Image src={logoSrc} style={styles.logo} /> : null}
        </View>

        {groups.length ? (
          <View style={styles.menuGrid}>
            {groups.map((group) => (
              <View key={group.category} style={styles.category}>
                <Text style={styles.categoryTitle}>{group.label}</Text>
                {group.products.map((product) => (
                  <View key={product.id || product.title} style={styles.productRow}>
                    <Text style={styles.productName}>{product.title}</Text>
                    <Text style={styles.productPrice}>{formatPrice(product.price, locale)}</Text>
                  </View>
                ))}
              </View>
            ))}
          </View>
        ) : (
          <Text style={styles.empty}>{labels.empty}</Text>
        )}

        <Text style={styles.footer}>{organization?.organization || "Comitty"}</Text>
      </Page>
    </Document>
  );
}
